import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant";
import { mapGetters } from "vuex";
import LessonApi from "@apis/editor.lesson.api";
import appConfigs from "@configs/app";
import { userGoogleCredential } from "@plugins/google/user.google.credential";
import { googleAuth } from "@plugins/google/google.auth";
import { PICKER_ERROR } from "@plugins/google/google.constant";
import { isEmpty } from "@utils/var.check";
import { ApiResponse } from "@apis/api";
import { GoogleDriveAccessData, LessonResourceApiModel } from "@plugins/google/types.ts";

export default {
  data() {
    return {
      skipAction: false,
    };
  },
  computed: {
    ...mapGetters("app", ["isIntegratorAppEnabled", "hasIntegratorAppCategoryAccess"]),
    hasGoogleMeetAccess() {
      return (
        this.isIntegratorAppEnabled(INTEGRATOR_APP_FEATURE_CATEGORY.GOOGLE_MEET) &&
        this.hasIntegratorAppCategoryAccess(INTEGRATOR_APP_FEATURE_CATEGORY.GOOGLE_MEET)
      );
    },
    hasGoogleDriveAccess() {
      return (
        this.isIntegratorAppEnabled(INTEGRATOR_APP_FEATURE_CATEGORY.GOOGLE_DRIVE) &&
        this.hasIntegratorAppCategoryAccess(INTEGRATOR_APP_FEATURE_CATEGORY.GOOGLE_DRIVE)
      );
    },
  },
  methods: {
    checkHasAnyGoogleAttachment(
      requestData: Partial<LessonResourceApiModel>[],
      googleDocumentRootFolderType: string,
    ): Promise<ApiResponse<boolean>> {
      return new Promise((resolve, reject) => {
        LessonApi.checkHasAnyGoogleAttachment(requestData, googleDocumentRootFolderType).then(
          (response) => {
            if (response.code === "ok") {
              resolve(response);
            }
          },
          (error) => reject(error),
        );
      });
    },
    showAppAvailabilityWarningModel() {
      return new Promise((resolve, reject) => {
        const props = {
          title: this.$i18n.t("google_app_availability_warning_title"),
          content: this.$i18n.t("google_app_availability_warning_content"),
          primaryBtnLabel: this.$i18n.t("button_confirm"),
          secondaryBtnLabel: this.$i18n.t("button_cancel"),
          actionPrimary: () => {
            resolve();
          },
          actionHidden: () => {
            reject();
          },
          actionHideRequest: () => {
            reject();
          },
        };
        this.$modal.showMessage("warning", props);
      });
    },
    showAppAvailabilityErrorModel() {
      this.actionLoading = false;
      return new Promise((resolve, reject) => {
        const props = {
          title: this.$i18n.t("google_app_availability_error_title"),
          content: this.$i18n.t("google_app_availability_error_content"),
          primaryBtnLabel: this.$i18n.t("button_confirm"),
          use_secondaryButton: false,
          actionPrimary: () => {
            reject();
          },
        };
        this.$modal.showMessage("warning", props);
      });
    },
    checkIfHasGoogleDriveAccess(
      showModal: () => Promise<void>,
      primaryAction: () => void | (() => Promise<void>),
      secondaryAction: () => void | (() => Promise<void>),
      data?: GoogleDriveAccessData,
    ) {
      if (!this.hasGoogleDriveAccess) {
        if (data) {
          this.checkHasAnyGoogleAttachment(data.requestData, data.googleDocumentRootFolderType)
            .then((response) => {
              if (response.data) {
                showModal().then(
                  () => {
                    primaryAction();
                  },
                  () => {
                    secondaryAction();
                  },
                );
              } else {
                primaryAction();
              }
            })
            .catch((error) => {
              this.handleApiError(error);
            });
        } else {
          this.$modal.removeAll();
          showModal().then(
            () => {
              primaryAction();
            },
            () => {
              secondaryAction();
            },
          );
        }
      } else {
        primaryAction();
      }
    },
    checkIfHasGoogleMeetAccess(showModal, primaryAction, secondaryAction) {
      if (!this.hasGoogleMeetAccess) {
        this.$modal.removeAll();
        showModal().then(
          () => {
            primaryAction();
          },
          () => {
            secondaryAction();
          },
        );
      } else {
        primaryAction();
      }
    },
    async checkUserLinked() {
      await userGoogleCredential.fetchLinkedAccountCredential();
      this.userCredential = userGoogleCredential.getCredential();
      if (isEmpty(this.userCredential.userLinkedEmail)) {
        return Promise.reject(PICKER_ERROR.EMPTY_EMAIL);
      }

      try {
        if (
          this.userCredential.isAccessTokenExpired &&
          !isEmpty(userGoogleCredential.accessToken)
        ) {
          await userGoogleCredential.refreshLinkedAccountCredential();
          this.userCredential = userGoogleCredential.getCredential();
        }
      } catch (error) {
        return Promise.reject(error);
      }

      if (isEmpty(googleAuth.currentUser)) {
        await googleAuth.checkAuthInstanceInitState();
      } else {
        await googleAuth.isEmailSync();
      }

      if (googleAuth.requireSignIn) {
        googleAuth.handleRequestCode();
        this.skipAction = true;
      }

      return Promise.resolve(true);
    },
    openNotLinkedModal(action) {
      let title;
      switch (action) {
        case "download":
          title = this.$i18n.t("tinymce_plugin_not_linked_download_modal_title");
          break;
        case "remove":
          title = this.$i18n.t("tinymce_plugin_not_linked_remove_modal_title");
          break;
        case "pdfMark":
          title = this.$i18n.t("tinymce_plugin_not_linked_mark_as_pdf_modal_title");
          break;
        case "googleMark":
          title = this.$i18n.t("tinymce_plugin_not_linked_mark_as_google_modal_title");
          break;
        case "googleMeet":
          title = this.$i18n.t("meeting_link_account_not_linked_modal_title");
          break;
        default:
          title = this.$i18n.t("tinymce_plugin_not_linked_open_in_new_tab_modal_title");
      }
      const props = {
        title: title,
        content: this.$i18n.t("tinymce_plugin_not_linked_modal_message", {
          linkAccountUrl: appConfigs.vleDomain + `/profile/linked-account`,
        }),
        primaryBtnLabel: this.$i18n.t("button_ok"),
        use_secondaryButton: false,
      };
      this.$modal.showMessage("warning", props);
    },
  },
};
